// frontend/src/components/Sidebar/Sidebar.jsx - VERSIÓN CON MÓDULO DE FACTURACIÓN

import React, { useState, useEffect } from 'react';
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import { Collapse, Badge } from 'reactstrap';
import { useAuth } from '../../contexts/AuthContext';
import { invoiceSchedulesAPI, cashflowAPI } from '../../services/api';

// ✅ Importar el logo
import masLogo from '../../assets/img/maslogosinletras.png';

function Sidebar({ routes }) {
  const location = useLocation();
  const { user, logout, hasPermission } = useAuth();
  
  // Estado para controlar qué submenús están abiertos
  const [collapseStates, setCollapseStates] = useState({});
  
  // ✅ Estados para contadores de notificaciones de facturación
  const [invoiceNotificationsCount, setInvoiceNotificationsCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  
  // ✅ Estado para controlar si el sidebar está desplegado
  const [isHovered, setIsHovered] = useState(false);
  
  // ✅ Estado para detectar si el usuario hizo clic
  const [userClicked, setUserClicked] = useState(false);

  // Inicializar estados de collapse
  useEffect(() => {
    const initialStates = {};
    routes.forEach(route => {
      if (route.state) {
        const isActive = route.submenu?.some(
          sub => location.pathname === sub.layout + sub.path
        );
        initialStates[route.state] = isActive;
      }
    });
    setCollapseStates(initialStates);
  }, [routes, location.pathname]);

  // ✅ Cargar notificaciones de facturación
  useEffect(() => {
    if (user && (hasPermission('view_finance') || hasPermission('create_invoices'))) {
      loadInvoiceNotifications();
      const interval = setInterval(loadInvoiceNotifications, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const loadInvoiceNotifications = async () => {
    try {
      // Cargar conteo de pendientes
      const response = await invoiceSchedulesAPI.getNotificationsCount();
      setInvoiceNotificationsCount(response.data.count || 0);
      setOverdueCount(response.data.overdue || 0);
    } catch (error) {
      console.error('Error cargando notificaciones:', error);
    }
  };

  // ✅ Configurar sidebar
  useEffect(() => {
    if (window.innerWidth >= 1200) {
      document.body.classList.add('g-sidenav-show');
    }

    return () => {
      document.body.classList.remove('g-sidenav-hidden');
    };
  }, []);

  // ✅ Detectar movimiento del mouse para cerrar sidebar
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isHovered && !userClicked && window.innerWidth >= 1200) {
        const halfScreen = window.innerWidth / 2;
        if (e.clientX > halfScreen) {
          setIsHovered(false);
        }
      }
    };

    if (isHovered && !userClicked) {
      document.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isHovered, userClicked]);

  // Cerrar sidebar en móvil al hacer clic en un link
  const handleLinkClick = () => {
    if (window.innerWidth < 1200) {
      document.body.classList.remove('g-sidenav-show');
    }
    if (window.innerWidth >= 1200) {
      setIsHovered(false);
      setUserClicked(false);
    }
  };

  // Toggle de submenú
  const toggleCollapse = (state) => {
    setCollapseStates(prev => ({
      ...prev,
      [state]: !prev[state]
    }));
  };

  const handleMouseEnter = () => {
    if (window.innerWidth >= 1200) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth >= 1200) {
      setIsHovered(false);
      setUserClicked(false);
    }
  };

  const handleSidebarClick = () => {
    setUserClicked(true);
  };

  // ✅ Obtener badge para submenú específico
  const getSubmenuBadge = (subProp) => {
    // Badge para Inbox de Facturación
    if (subProp.path === '/finance/inbox' && invoiceNotificationsCount > 0) {
      return (
        <Badge 
          color={overdueCount > 0 ? 'danger' : 'warning'} 
          pill 
          className="ms-auto"
          style={{ fontSize: '0.6rem' }}
        >
          {invoiceNotificationsCount}
        </Badge>
      );
    }
    
    // Badge para Cashflow (si hay vencidas)
    if (subProp.path === '/finance/cashflow' && overdueCount > 0) {
      return (
        <Badge 
          color="danger" 
          pill 
          className="ms-auto"
          style={{ fontSize: '0.6rem' }}
          title={`${overdueCount} facturas vencidas`}
        >
          !
        </Badge>
      );
    }
    
    return null;
  };

  // Crear links del sidebar
  // Crear links del sidebar
  // Crear links del sidebar
  const createLinks = (routes) => {
    return routes
      .filter((route) => {
        // ✅ Ocultar rutas marcadas como hidden
        if (route.hidden) return false;
        
        // ✅ Si la ruta tiene permiso requerido, verificar
        if (route.permission) {
          return hasPermission(route.permission);
        }
        
        // ✅ Si tiene submenú, verificar si tiene acceso a AL MENOS UN subitem
        if (route.submenu) {
          // Filtrar subitems a los que tiene acceso
          const accessibleSubmenu = route.submenu.filter(sub => 
            !sub.permission || hasPermission(sub.permission)
          );
          
          // Solo mostrar el menú si tiene acceso a algún subitem
          return accessibleSubmenu.length > 0;
        }
        
        // ✅ Si no tiene permiso ni submenú, mostrar por defecto
        return true;
      })
      .map((prop, key) => {
        // SI TIENE SUBMENÚ
        if (prop.submenu) {
          const isOpen = collapseStates[prop.state];
          const isFinanceMenu = prop.name === 'Facturación';
          const totalBadgeCount = isFinanceMenu ? invoiceNotificationsCount : 0;
          
          return (
            <li className="nav-item" key={key}>
              <a
                className="nav-link"
                href="#!"
                onClick={(e) => {
                  e.preventDefault();
                  toggleCollapse(prop.state);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="icon icon-shape icon-sm border-radius-md text-center me-2 d-flex align-items-center justify-content-center">
                  <i className={`${prop.icon} text-dark text-sm opacity-10`}></i>
                </div>
                <span className="nav-link-text ms-1">{prop.name}</span>
                
                {/* ✅ Badge para menú de Facturación */}
                {isFinanceMenu && totalBadgeCount > 0 && (
                  <Badge 
                    color={overdueCount > 0 ? 'danger' : 'warning'} 
                    pill 
                    className="ms-auto me-2"
                    style={{ fontSize: '0.65rem' }}
                  >
                    {totalBadgeCount}
                  </Badge>
                )}
                
                <i 
                  className={`fas fa-chevron-${isOpen ? 'up' : 'down'} ${totalBadgeCount > 0 ? '' : 'ms-auto'} text-xs`}
                  style={{ transition: 'transform 0.3s' }}
                ></i>
              </a>
              
              <Collapse isOpen={isOpen}>
                <ul className="nav ms-4 ps-3">
                  {prop.submenu
                    .filter(sub => !sub.permission || hasPermission(sub.permission))
                    .map((subProp, subKey) => {
                      const isActive = location.pathname === subProp.layout + subProp.path;
                      const badge = getSubmenuBadge(subProp);
                      
                      return (
                        <li className="nav-item" key={subKey}>
                          <RouterNavLink
                            to={subProp.layout + subProp.path}
                            className={({ isActive }) =>
                              isActive ? 'nav-link active' : 'nav-link'
                            }
                            end={subProp.path === '/finance'}
                            onClick={handleLinkClick}
                          >
                            <span className="sidenav-mini-icon">{subProp.miniName}</span>
                            <span className="sidenav-normal ms-2">{subProp.name}</span>
                            {badge}
                          </RouterNavLink>
                        </li>
                      );
                    })}
                </ul>
              </Collapse>
            </li>
          );
        }
        
        // LINK NORMAL (sin submenú)
        const isActive = location.pathname === prop.layout + prop.path;
        
        return (
          <li className="nav-item" key={key}>
            <RouterNavLink
              to={prop.layout + prop.path}
              className={isActive ? 'nav-link active' : 'nav-link'}
              onClick={handleLinkClick}
            >
              <div className="icon icon-shape icon-sm border-radius-md text-center me-2 d-flex align-items-center justify-content-center">
                <i className={`${prop.icon} text-${isActive ? 'white' : 'dark'} text-sm opacity-10`}></i>
              </div>
              <span className="nav-link-text ms-1">{prop.name}</span>
            </RouterNavLink>
          </li>
        );
      });
  };

  return (
    <>
      {/* ✅ Hint visual cuando el sidebar está oculto */}
      {!isHovered && window.innerWidth >= 1200 && (
        <div 
          className="sidebar-hint"
          onMouseEnter={handleMouseEnter}
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            width: '12px',
            height: '100vh',
            background: 'linear-gradient(135deg, #038d81 0%, #02574e 100%)',
            zIndex: 1050,
            cursor: 'pointer',
            transition: 'width 0.2s ease, background 0.2s ease',
            boxShadow: '2px 0 8px rgba(3, 141, 129, 0.3)'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.width = '20px';
            e.currentTarget.style.background = 'linear-gradient(135deg, #02a195 0%, #037066 100%)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.width = '12px';
            e.currentTarget.style.background = 'linear-gradient(135deg, #038d81 0%, #02574e 100%)';
          }}
        >
          {/* Indicador adicional */}
          <div 
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(3, 141, 129, 0.95)',
              padding: '12px 6px',
              borderRadius: '0 10px 10px 0',
              boxShadow: '3px 0 12px rgba(0,0,0,0.2)',
              transition: 'all 0.2s ease'
            }}
          >
            <i 
              className="fas fa-chevron-right text-white" 
              style={{ fontSize: '14px' }}
            ></i>
          </div>
          
          {/* ✅ Badge de notificaciones en el hint */}
          {invoiceNotificationsCount > 0 && (
            <div 
              style={{
                position: 'absolute',
                right: '-6px',
                top: '30px',
                background: overdueCount > 0 ? '#dc3545' : '#ffc107',
                color: overdueCount > 0 ? 'white' : 'black',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 'bold',
                boxShadow: '0 2px 6px rgba(220, 53, 69, 0.4)',
                border: '2px solid white'
              }}
            >
              {invoiceNotificationsCount > 9 ? '9+' : invoiceNotificationsCount}
            </div>
          )}
        </div>
      )}

      <aside
        className={`sidenav navbar navbar-vertical navbar-expand-xs border-0 border-radius-xl my-3 fixed-start bg-white ${isHovered ? 'sidebar-visible' : 'sidebar-hidden'}`}
        id="sidenav-main"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleSidebarClick}
        style={{
          left: isHovered ? '1rem' : '-280px',
          transition: 'left 0.25s ease-out',
          zIndex: 1040,
          boxShadow: isHovered ? '0 8px 26px -4px rgba(20,20,20,0.15), 0 8px 9px -5px rgba(20,20,20,0.06)' : 'none'
        }}
      >
        {/* Header del Sidebar con LOGO MAS+ */}
        <div className="sidenav-header">
          <i
            className="fas fa-times p-3 cursor-pointer text-secondary opacity-5 position-absolute end-0 top-0 d-xl-none"
            aria-hidden="true"
            onClick={() => {
              document.body.classList.remove('g-sidenav-show');
            }}
          ></i>
          <RouterNavLink 
            className="navbar-brand m-0 p-0" 
            to="/admin/dashboard"
            onClick={handleLinkClick}
          >
            <div className="d-flex align-items-center px-3 py-2">
              <img 
                src={masLogo} 
                alt="MAS+ Logo" 
                style={{ 
                  height: '40px',
                  width: 'auto',
                  objectFit: 'contain'
                }}
              />
              <span className="ms-2 font-weight-bold text-dark">
                Consultoría MAS+
              </span>
            </div>
          </RouterNavLink>
        </div>

        <hr className="horizontal dark mt-0 mb-2" />

        {/* Navegación principal */}
        <div 
          className="collapse navbar-collapse w-auto h-auto" 
          id="sidenav-collapse-main"
          style={{ 
            maxHeight: 'calc(100vh - 280px)',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          <ul className="navbar-nav">
            {createLinks(routes)}
          </ul>
        </div>

        {/* Footer con info de usuario */}
        <div className="sidenav-footer position-absolute w-100" style={{ bottom: 0 }}>
          <hr className="horizontal dark my-2" />
          
          <div className="px-3 py-2">
            <div className="d-flex align-items-center">
              {user?.profile_picture ? (
                <img 
                  src={user.profile_picture} 
                  alt={user.first_name}
                  className="avatar avatar-sm rounded-circle me-2"
                />
              ) : (
                <div className="avatar avatar-sm bg-gradient-primary rounded-circle me-2 d-flex align-items-center justify-content-center">
                  <i className="ni ni-single-02 text-white text-sm"></i>
                </div>
              )}
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <h6 className="mb-0 text-sm text-truncate">
                  {user?.first_name} {user?.last_name}
                </h6>
                <p className="text-xs text-secondary mb-0 text-truncate">
                  {user?.email}
                </p>
              </div>
            </div>
            
            <div className="mt-2">
              <span className="badge badge-sm bg-gradient-primary w-100">
                {user?.role === 'super_admin' ? 'Super Admin' :
                 user?.role === 'admin' ? 'Administrador' : 
                 user?.role === 'sales' ? 'Ventas' :
                 user?.role === 'finance' ? 'Finanzas' :
                 user?.role === 'viewer' ? 'Visualizador' : user?.role}
              </span>
            </div>
            
            <div className="mt-2 d-flex gap-1">
              

              <button
                onClick={logout}
                className="btn btn-outline-danger btn-sm flex-grow-1"
                title="Cerrar Sesión"
              >
                <i className=""></i>
                CERRAR SESION
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* CSS ADICIONAL */}
      <style>{`
        @media (min-width: 1200px) {
          .sidebar-hidden ~ .main-content {
            margin-left: 0 !important;
          }

          .sidebar-visible {
            left: 1rem !important;
          }

          .sidenav {
            transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .sidenav:not(:hover) {
            transition-delay: 0.15s;
          }

          .sidebar-visible::before {
            content: '';
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.05);
            z-index: -1;
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          .sidebar-visible:hover::before {
            opacity: 1;
          }
        }

        .sidenav {
          overflow: visible !important;
        }

        #sidenav-collapse-main {
          overflow-y: auto !important;
          overflow-x: hidden !important;
        }

        #sidenav-collapse-main::-webkit-scrollbar {
          width: 6px;
        }

        #sidenav-collapse-main::-webkit-scrollbar-track {
          background: transparent;
        }

        #sidenav-collapse-main::-webkit-scrollbar-thumb {
          background: rgba(3, 141, 129, 0.3);
          border-radius: 10px;
        }

        #sidenav-collapse-main::-webkit-scrollbar-thumb:hover {
          background: rgba(3, 141, 129, 0.5);
        }

        .nav-item .collapse {
          transition: all 0.3s ease-in-out;
        }

        .sidenav-footer {
          background: white;
          z-index: 10;
        }

        @media (max-width: 1199px) {
          .sidenav {
            left: 0 !important;
          }

          .sidebar-hint {
            display: none !important;
          }

          .sidenav-footer {
            position: relative !important;
          }
        }

        .sidebar-hint:hover {
          background: linear-gradient(135deg, #02a195 0%, #037066 100%);
        }
      `}</style>
    </>
  );
}

export default Sidebar;
